/**
 * A PAYOUT'S STATUS, IN WORDS AND AS A PILL - shared by the Statements list and the monthly payouts
 * table on /payments (design 15, the owner's live look at real cabinets, 2026-10-01: the prototype
 * draws a statement's state as a small pill - "PAID", "PROCESSING", "ROLLED OVER").
 *
 * THE WORDS ARE THE CONTRACT'S SEVEN AND NONE IS INVENTED. `payouts[].status` and a statement row's
 * `status` are the same enum, [pending, sent, paid, rejected, failed, disputed, cancelled], or null.
 * `sent` is NOT "Sent": it carries no `paidAt` (only the `paid` move writes one), and the payments
 * screen says "Sent" only with `paidAt` (design 14, F14). `rejected` is not "by the payment
 * provider": `pending -> rejected` is an operator putting the money back before any payment is
 * attempted.
 *
 * COLOUR IS NEVER THE ONLY SIGNAL, AND NEVER THE INK. Each pill carries its words; the tint and the
 * dot are a second channel. The words are `--mx-text` on the tint, the one ink that clears 4.5:1 on
 * every tint over the card in both schemes (the design 15 brief: tinted badges composite darker on
 * the card, and the editor's badges failed at 3.99 and 4.34 in light with a tone as their ink).
 */
import { i18n } from '../../i18n';
import classes from './payments.module.css';

export type PayoutStatus =
  'pending' | 'sent' | 'paid' | 'rejected' | 'failed' | 'disputed' | 'cancelled';

const PAYOUT_STATUSES: ReadonlySet<string> = new Set<PayoutStatus>([
  'pending',
  'sent',
  'paid',
  'rejected',
  'failed',
  'disputed',
  'cancelled',
]);

function isPayoutStatus(status: string): status is PayoutStatus {
  return PAYOUT_STATUSES.has(status);
}

/** A status the contract does not have yet is shown as itself rather than dropped. */
export function payoutStatusWords(status: string): string {
  return isPayoutStatus(status) ? i18n.t(`payments:status.${status}`) : status;
}

type Tone = 'done' | 'moving' | 'problem' | 'held' | 'quiet';

const TONE_BY_STATUS: Readonly<Record<string, Tone>> = {
  paid: 'done',
  pending: 'moving',
  sent: 'moving',
  disputed: 'held',
  failed: 'problem',
  rejected: 'problem',
  cancelled: 'quiet',
};

const TONE_CLASS: Readonly<Record<Tone, string | undefined>> = {
  done: classes.pillDone,
  moving: classes.pillMoving,
  problem: classes.pillProblem,
  held: classes.pillHeld,
  quiet: classes.pillQuiet,
};

/**
 * The status as a pill. `words` replaces the status's own words where the caller knows better - a
 * run with no status was worked out and not approved, or was not payable; a statement with none
 * sent nothing.
 */
export function PayoutPill({
  status,
  words,
}: {
  readonly status: string | null | undefined;
  readonly words?: string;
}) {
  const tone: Tone =
    status === undefined || status === null ? 'quiet' : (TONE_BY_STATUS[status] ?? 'quiet');
  const text =
    words ??
    (status === undefined || status === null
      ? i18n.t('payments:status.nothingSent')
      : payoutStatusWords(status));
  return (
    <span className={`${classes.pill ?? ''} ${TONE_CLASS[tone] ?? ''}`}>
      <span className={classes.pillDot} aria-hidden="true" />
      {text}
    </span>
  );
}
